import {
  Box,
  Button,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import CustomSnackbar from 'components/CustomSnackbar';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import MDBox from '../../../components/MDBox';
import MDTypography from '../../../components/MDTypography';
import DashboardNavbar from '../../../examples/Navbars/DashboardNavbar';
import DashboardLayout from '../../LayoutContainers/DashboardLayout';
import { putSchedule, getScheduleMeta } from '../../../components/Schedule/schedulesCache.js';
import {
  fetchControllers as fetchDevices,
  fetchSchedule,
  saveSchedule,
  saveRecommendationForDevices,
} from '../../../stores/sensor/controllersSlice';
import { getBuildings } from '../../../stores/sensor/sensorStore';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { filterDevices, getSensorOptions } from '../../../helpers/filterHelpers';
import { brandColors } from 'types/colors';
import ScheduleMetaDataForm from './ScheduleMetaDataForm';
import EditSchedulePatternsContainer from './EditSchedulePatternsContainer';
import DeviceDetails from './DeviceDetails';

const getScheduleDeviceIds = schedule =>
  Array.isArray(schedule?.devices) ? schedule.devices.map(device => device.id).filter(Boolean) : schedule?.device_ids || [];

const ScheduleForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { scheduleId } = useParams();
  const numericScheduleId = scheduleId ? Number(scheduleId) : null;
  const [scheduleName, setScheduleName] = useState('');
  const [loadedSchedule, setLoadedSchedule] = useState(false);
  const [scheduleLoadError, setScheduleLoadError] = useState(null);
  const [scheduleMeta, setScheduleMeta] = useState(() =>
    Number.isFinite(numericScheduleId) ? getScheduleMeta(numericScheduleId) : null,
  );
  const [localSelectedControllers, setLocalSelectedControllers] = useState([]); // array of ids only
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(true);
  const [collapsedSections, setCollapsedSections] = useState({
    scheduleEditor: true,
    devicesAssign: true,
    scheduleProperties: true,
    deviceDetails: true,
  });
  const scheduleTableRef = useRef(null);
  const scheduleMetaFormRef = useRef(null);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [changedFlags, setChangedFlags] = useState({
    patterns: false,
    metadata: false,
    devices: false,
  });
  const isChanged = changedFlags.patterns || changedFlags.metadata || changedFlags.devices;

  const [isSaved, setIsSaved] = useState(false);
  const sectionWidth = { xs: '100%', lg: '85%', xl: '70%' };
  const devices = useSelector(s => s.controllers.controllers || []);
  const { selectedFilterObject } = useSelector(s => s.persistence);
  const scheduleDeviceIds = useMemo(() => getScheduleDeviceIds(scheduleMeta), [scheduleMeta]);
  const searchString = useSelector(s => s.persistence.searchString || '');
  const currentUser = useSelector(s => s.sensors.currentUser);
  const { distributor, customer, building, floor, area, status } = selectedFilterObject || {};
  const [hasInitializedDevices, setHasInitializedDevices] = useState(false);

  // --- filtered controllers ---
  const filteredDevices = useMemo(() => {
    if (devices.length === 0) return [];
    return filterDevices(
      devices,
      {
        distributor,
        customer,
        building,
        floor,
        area,
        status,
        searchString,
      },
      currentUser,
    ).filter(d => d.device_type?.startsWith('Controller'));
  }, [devices, distributor, customer, building, floor, area, status, searchString]);

  // get Device details
  const scheduleDevice = useMemo(() => {
    if (scheduleMeta?.is_global || !scheduleMeta) {
      return null;
    }
    const deviceId = scheduleDeviceIds[0];

    return devices.find(d => d.id === deviceId) || null;
  }, [scheduleMeta?.is_auto_generated, scheduleDeviceIds, devices]);

  // cache a stable, sorted list of available IDs for effect deps
  const availableControllersIds = useMemo(
    () =>
      (filteredDevices || [])
        .map(d => d.id)
        .filter(Boolean)
        .sort(),
    [filteredDevices],
  );

  const sensorOptions = useMemo(() => getSensorOptions(filteredDevices), [filteredDevices]);
  const scheduleDeviceIdsForPresetFiltering = useMemo(() => {
    const ids = localSelectedControllers.length ? localSelectedControllers : scheduleDeviceIds;
    return [...new Set(ids.map(id => String(id)))];
  }, [localSelectedControllers, scheduleDeviceIds]);

  const scheduleDevicesForPresetFiltering = useMemo(() => {
    const devicesById = new Map(devices.map(device => [String(device.id), device]));
    const scheduleMetaDevicesById = new Map((scheduleMeta?.devices || []).map(device => [String(device.id), device]));
    return scheduleDeviceIdsForPresetFiltering
      .map(deviceId => devicesById.get(deviceId) || scheduleMetaDevicesById.get(deviceId))
      .filter(Boolean);
  }, [devices, scheduleDeviceIdsForPresetFiltering, scheduleMeta?.devices]);

  const showRelayPresets = useMemo(() => {
    if (!scheduleDevicesForPresetFiltering.length) return false;

    return scheduleDevicesForPresetFiltering.every(device =>
      String(device.device_type ?? '')
        .toLowerCase()
        .includes('relay'),
    );
  }, [scheduleDevicesForPresetFiltering]);

  const markPatternsChanged = useCallback(() => {
    setChangedFlags(prev => ({ ...prev, patterns: true }));
    setIsSaved(false);
  }, []);

  const markMetadataChanged = useCallback(() => {
    setChangedFlags(prev => ({ ...prev, metadata: true }));
    setIsSaved(false);
  }, []);

  const markDevicesChanged = useCallback(() => {
    setChangedFlags(prev => ({ ...prev, devices: true }));
    setIsSaved(false);
  }, []);

  // selection handlers
  const handleControllerChange = useCallback(selectedControllerIds => {
    setLocalSelectedControllers(selectedControllerIds);
    markDevicesChanged();
  }, [markDevicesChanged]);

  const handleRegenerateRecommendation = useCallback(async () => {
    if (!scheduleDeviceIds.length) {
      setSnack({
        open: true,
        message: 'No device associated with this recommendation',
        severity: 'error',
      });
      return;
    }

    setIsSaving(true);
    try {
      const cleanDeviceIds = [...new Set(scheduleDeviceIds.map(id => Number(id)))];

      await dispatch(
        saveRecommendationForDevices({
          deviceIds: cleanDeviceIds,
        }),
      ).unwrap();

      setSnack({
        open: true,
        message: 'Recommendation regenerated successfully',
        severity: 'success',
      });

      // Refresh the schedule data to get updated patterns
      if (numericScheduleId) {
        const scheduleData = await dispatch(fetchSchedule(numericScheduleId)).unwrap();
        putSchedule(scheduleData);
        setScheduleMeta(getScheduleMeta(numericScheduleId));
      }

      navigate('/manage-schedules/');
    } catch (error) {
      setSnack({
        open: true,
        message: `Failed to regenerate recommendation: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
      navigate('/manage-schedules/');
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, scheduleDeviceIds, numericScheduleId]);

  useEffect(() => {
    if (hasInitializedDevices) return;
    if (!loadedSchedule) return;
    if (!numericScheduleId) return;
    if (!scheduleDeviceIds.length) return;
    if (!Array.isArray(filteredDevices) || filteredDevices.length === 0) return;

    // Get device IDs from schedule metadata and ensure they exist in filtered list
    const deviceIdsSet = new Set(filteredDevices.map(d => d.id));
    const validDeviceIds = scheduleDeviceIds.filter(id => deviceIdsSet.has(id));

    // Initialize selection from schedule's device_ids
    setLocalSelectedControllers(validDeviceIds);
    setHasInitializedDevices(true);
  }, [scheduleDeviceIds, numericScheduleId, loadedSchedule, filteredDevices, hasInitializedDevices]);

  const toggleSection = sectionKey => {
    setCollapsedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const handlePaperClick = sectionKey => {
    if (collapsedSections[sectionKey]) {
      toggleSection(sectionKey);
    }
  };

  const handleHeaderKeyDown = (event, sectionKey) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleSection(sectionKey);
    }
  };

  const renderSectionHeader = (label, sectionKey, infoText) => {
    const isCollapsed = collapsedSections[sectionKey];
    return (
      <Box
        role="button"
        tabIndex={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: isCollapsed ? 0 : 2,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={event => {
          event.stopPropagation();
          toggleSection(sectionKey);
        }}
        onKeyDown={event => handleHeaderKeyDown(event, sectionKey)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h4">{label}</Typography>
          {infoText ? (
            <Tooltip title={infoText} arrow>
              <IconButton size="small" aria-label={`${label} info`} onClick={event => event.stopPropagation()}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
        {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
      </Box>
    );
  };

  // Initialize data on component mount
  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      setLoadedSchedule(false);
      setScheduleLoadError(null);

      try {
        dispatch(fetchDevices('Controller'));
        dispatch(getBuildings());

        if (!scheduleId) {
          if (isMounted) {
            setLoadedSchedule(true);
          }
          return;
        }

        const scheduleData = await dispatch(fetchSchedule(scheduleId)).unwrap();

        if (!isMounted) return;

        putSchedule(scheduleData);
        setScheduleName(scheduleData.schedule_name || '');
        setScheduleMeta(Number.isFinite(numericScheduleId) ? getScheduleMeta(numericScheduleId) : null);
        setLoadedSchedule(true);
      } catch (error) {
        if (!isMounted) return;
        console.error('❌ ScheduleForm: Failed to initialize data:', error);
        setScheduleLoadError(error);
      }
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [dispatch, scheduleId, numericScheduleId]);

  useEffect(() => {
    setScheduleMeta(Number.isFinite(numericScheduleId) ? getScheduleMeta(numericScheduleId) : null);
  }, [numericScheduleId]);

  const handleSearchClick = useCallback(() => {}, [localSelectedControllers]);

  // update only changed sections
  const handleSaveSchedule = useCallback(async () => {
    const payload = {};

    if (numericScheduleId) {
      const scheduleDataFromForm = changedFlags.metadata ? (scheduleMetaFormRef.current?.getFormData?.() ?? {}) : {};
      payload.scheduleData = {
        ...scheduleDataFromForm,
        id: numericScheduleId,
        schedule_id: numericScheduleId,
        status: 'active_not_synced',
        ...(changedFlags.patterns ? { is_auto_generated: false } : {}),
      };

      if (changedFlags.metadata && !payload.scheduleData.schedule_name) {
        setSnack({
          open: true,
          message: 'Please provide a schedule name.',
          severity: 'error',
        });
        return;
      }
    }

    if (changedFlags.patterns) {
      payload.patterns = scheduleTableRef.current?.getPatterns?.() ?? [];
    }

    if (changedFlags.devices) {
      payload.deviceIds = localSelectedControllers;
    }

    setIsSaving(true);
    try {
      const result = await dispatch(saveSchedule(payload)).unwrap();

      if (result?.schedule) {
        putSchedule(result.schedule);
        setScheduleMeta(getScheduleMeta(numericScheduleId));
      }

      setSnack({
        open: true,
        message: 'Schedule saved successfully.',
        severity: 'success',
      });

      setChangedFlags({ patterns: false, metadata: false, devices: false });
      setIsSaved(true);
    } catch (error) {
      setSnack({
        open: true,
        message: 'Failed to save schedule. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, localSelectedControllers, numericScheduleId, scheduleMeta, changedFlags]);

  if (!loadedSchedule && !scheduleLoadError) {
    return (
      <DashboardLayout>
        <DashboardNavbar objectName="" />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '40vh',
          }}>
          <CircularProgress color="info" />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar objectName="Edit schedule" />
      <Dialog open={isInfoModalOpen} onClose={() => setIsInfoModalOpen(true)}>
        <DialogTitle>Reminder</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#000000cb' }}>
            Changes to this schedule will apply to all controllers where it's active.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIsInfoModalOpen(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      {scheduleLoadError && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '20vh',
          }}>
          <Typography color="error">Failed to load schedule. Please try again.</Typography>
        </Box>
      )}
      {loadedSchedule && !scheduleLoadError && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          {/* Header */}
          <MDBox
            my={1}
            px={2}
            py={2}
            variant="gradient"
            style={{
              background: '#3244E2',
              width: '100%',
            }}
            borderRadius="lg"
            coloredShadow="info">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" marginLeft={2} spacing={2} alignItems="end">
                <MDTypography variant="h4" color="light">
                  Edit schedule: {scheduleName || scheduleMeta?.schedule_name || ''}
                </MDTypography>
              </Stack>
            </Stack>
          </MDBox>
          <Box
            sx={{
              width: sectionWidth,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 3,
            }}>
            <Paper
              elevation={1}
              className="scheduleProperties"
              onClick={() => handlePaperClick('scheduleProperties')}
              sx={{
                borderRadius: '1rem',
                flex: { xs: '1 1 100%', lg: '1 1 50%' },
                width: { xs: '100%', lg: '50%' },
                padding: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                cursor: collapsedSections.scheduleProperties ? 'pointer' : 'default',
              }}>
              {renderSectionHeader('Schedule Properties', 'scheduleProperties')}
              <Collapse in={!collapsedSections.scheduleProperties}>
                <ScheduleMetaDataForm
                  ref={scheduleMetaFormRef}
                  initialData={scheduleMeta}
                  isRecommendation={scheduleMeta?.is_auto_generated}
                  onChange={markMetadataChanged}
                  currentDeviceIds={localSelectedControllers}
                  originalDeviceIds={scheduleDeviceIds}
                  scheduleId={numericScheduleId}
                  onActivationSuccess={message => {
                    setSnack({ open: true, message, severity: 'success' });
                    // Refresh schedule data
                    dispatch(fetchSchedule(scheduleId));
                  }}
                  onActivationError={message => {
                    setSnack({ open: true, message, severity: 'error' });
                  }}
                />
              </Collapse>
            </Paper>

            <Paper
              elevation={1}
              className="deviceDetails"
              onClick={() => handlePaperClick(scheduleMeta?.is_auto_generated ? 'deviceDetails' : 'devicesSelection')}
              sx={{
                borderRadius: '1rem',
                flex: { xs: '1 1 100%', lg: '1 1 50%' },
                width: { xs: '100%', lg: '50%' },
                padding: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                cursor: collapsedSections[scheduleMeta?.is_auto_generated ? 'deviceDetails' : 'devicesSelection']
                  ? 'pointer'
                  : 'default',
              }}>
              {!scheduleMeta?.is_global ? (
                <>
                  {renderSectionHeader('Device Details', 'deviceDetails')}
                  <Collapse in={!collapsedSections.deviceDetails}>
                    <DeviceDetails device={scheduleDevice} />
                  </Collapse>
                </>
              ) : (
                <>
                  {renderSectionHeader(
                    'Assign Devices',
                    'devicesAssign',
                    'Select the devices on which this schedule will be available.',
                  )}
                  <Collapse in={!collapsedSections.devicesAssign}>
                    <DevicesListFilter
                      status={false}
                      search={false}
                      sensorsSelect={true}
                      selectedSensors={localSelectedControllers}
                      onSensorChange={handleControllerChange}
                      sensorOptions={sensorOptions}
                      showSearchButton={false}
                      onSearchClick={handleSearchClick}
                      flexDirection="column"
                    />
                  </Collapse>
                </>
              )}
            </Paper>
          </Box>
          <Paper
            elevation={1}
            onClick={() => handlePaperClick('scheduleEditor')}
            sx={{
              p: '1.5rem',
              borderRadius: '1rem',
              backgroundColor: '#fff',
              width: sectionWidth,
              alignSelf: 'center',
              cursor: collapsedSections.scheduleEditor ? 'pointer' : 'default',
            }}>
            {renderSectionHeader('Schedule Editor', 'scheduleEditor')}
            <Collapse in={!collapsedSections.scheduleEditor}>
              <Box sx={{ mt: 1 }}>
                <EditSchedulePatternsContainer
                  scheduleTableRef={scheduleTableRef}
                  onPatternsChange={markPatternsChanged}
                  showRelayPresets={showRelayPresets}
                  devices={scheduleDevicesForPresetFiltering}
                />
              </Box>
            </Collapse>
          </Paper>
          <Box
            sx={{
              margin: {
                xs: '0px 0px',
                md: '0px 10rem',
                lg: '0px 22rem',
                xl: '0px 30rem',
                xxl: '0px 42rem',
              },
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveSchedule}
              disabled={scheduleMeta?.is_auto_generated ? false : !isChanged || isSaved}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}>
              {isSaving ? 'Processing…' : isSaved ? 'SAVED' : 'SAVE SCHEDULE'}
            </Button>
            {/* Back Button */}
            <Button variant="text" onClick={() => navigate('/manage-schedules')} sx={{ mt: 1 }}>
              ← Back to schedules List
            </Button>
          </Box>
        </Box>
      )}
      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={3000}
        vertical="top"
        horizontal="left"
        color="#fff"
      />
    </DashboardLayout>
  );
};

export default ScheduleForm;
